import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal, X } from "lucide-react";

type FilterType = "string" | "boolean" | "date" | "number" | "enum";

interface FilterConfig {
  key: string;
  type: FilterType;
  label: string;
  operators?: string[];
  options?: Array<{ value: string; label: string }>;
}

interface FilterValue {
  field: string;
  operator: string;
  value: string;
}

interface Props {
  filters: FilterConfig[];
  value: FilterValue[];
  onApply: (filters: FilterValue[]) => void;
  onClear: () => void;
}

const DEFAULT_OPERATORS: Record<FilterType, string[]> = {
  string: ["cont", "eq", "not_eq"],
  boolean: ["eq", "not_eq"],
  date: ["eq", "gt", "lt"],
  number: ["eq", "gt", "lt"],
  enum: ["eq", "not_eq"],
};

const OPERATOR_LABELS: Record<string, string> = {
  cont: "يحتوي",
  eq: "يساوي",
  not_eq: "لا يساوي",
  gt: "بعد",
  lt: "قبل",
};

const EMPTY_FILTER: FilterValue = { field: "", operator: "eq", value: "" };

export default function ResourceFiltersPopover({ filters, value, onApply, onClear }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValue[]>(value.length ? value : [{ ...EMPTY_FILTER }]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(value.length ? value : [{ ...EMPTY_FILTER }]);
    }
    setOpen(nextOpen);
  };

  const filterMap = useMemo(() => {
    return new Map(filters.map((filter) => [filter.key, filter]));
  }, [filters]);

  const filterCount = value.filter((item) => item.field && item.value !== "").length;

  const updateDraft = (index: number, next: Partial<FilterValue>) => {
    setDraft((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...next } : item)));
  };

  const removeDraft = (index: number) => {
    setDraft((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length ? next : [{ ...EMPTY_FILTER }];
    });
  };

  const addDraft = () => {
    setDraft((prev) => [...prev, { ...EMPTY_FILTER }]);
  };

  const handleApply = () => {
    const sanitized = draft
      .map((item) => ({
        field: item.field,
        operator: item.operator || "eq",
        value: item.value,
      }))
      .filter((item) => item.field && item.value !== "");

    onApply(sanitized);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="group inline-flex items-center rounded-md bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80">
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 items-center gap-2 px-3 text-sm font-medium text-inherit"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span>فلتر</span>
              {filterCount > 0 && (
                <span className="rounded-full bg-secondary-foreground/15 px-2 text-xs text-inherit">
                  {filterCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[520px] p-4">
            <div className="space-y-3">
              {draft.map((item, index) => {
                const config = filterMap.get(item.field);
                const operators = config?.operators || DEFAULT_OPERATORS[config?.type || "string"];
                return (
                  <div
                    key={`${item.field}-${index}`}
                    className="grid grid-cols-[1.1fr_0.9fr_1.2fr_auto] gap-2"
                  >
                    <select
                      className="h-9 rounded-md border border-input bg-input px-3 text-sm"
                      value={item.field}
                      onChange={(event) => {
                        const nextField = event.target.value;
                        const nextConfig = filterMap.get(nextField);
                        const nextOperators =
                          nextConfig?.operators || DEFAULT_OPERATORS[nextConfig?.type || "string"];
                        updateDraft(index, { field: nextField, operator: nextOperators[0] });
                      }}
                    >
                      <option value="">اختر الحقل</option>
                      {filters.map((filter) => (
                        <option key={filter.key} value={filter.key}>
                          {filter.label}
                        </option>
                      ))}
                    </select>

                    <select
                      className="h-9 rounded-md border border-input bg-input px-3 text-sm"
                      value={item.operator}
                      onChange={(event) => updateDraft(index, { operator: event.target.value })}
                      disabled={!item.field}
                    >
                      {operators.map((operator) => (
                        <option key={operator} value={operator}>
                          {OPERATOR_LABELS[operator] || operator}
                        </option>
                      ))}
                    </select>

                    {config?.type === "boolean" ? (
                      <select
                        className="h-9 rounded-md border border-input bg-input px-3 text-sm"
                        value={item.value}
                        onChange={(event) => updateDraft(index, { value: event.target.value })}
                        disabled={!item.field}
                      >
                        <option value="">اختر</option>
                        <option value="true">نعم</option>
                        <option value="false">لا</option>
                      </select>
                    ) : config?.type === "enum" ? (
                      <select
                        className="h-9 rounded-md border border-input bg-input px-3 text-sm"
                        value={item.value}
                        onChange={(event) => updateDraft(index, { value: event.target.value })}
                        disabled={!item.field}
                      >
                        <option value="">اختر</option>
                        {(config.options || []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={
                          config?.type === "date"
                            ? "date"
                            : config?.type === "number"
                              ? "number"
                              : "text"
                        }
                        value={item.value}
                        onChange={(event) => updateDraft(index, { value: event.target.value })}
                        placeholder="القيمة"
                        disabled={!item.field}
                      />
                    )}

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => removeDraft(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}

              <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                <Button type="button" variant="ghost" onClick={addDraft}>
                  + إضافة فلتر
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      onClear();
                      setOpen(false);
                    }}
                  >
                    مسح الفلاتر
                  </Button>
                  <Button type="button" onClick={handleApply}>
                    تطبيق الفلاتر
                  </Button>
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <>
          <span className="h-9 w-px bg-secondary-foreground/20" />
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground"
            onClick={onClear}
          >
            <X className="h-4 w-4" />
          </button>
        </>
      </div>
    </div>
  );
}
